import React from "react";
import { formatMessage } from "lib/globalize-util";
import { merge, $ } from "glamor";
import Typography from "@material-ui/core/Typography";
import {
    CollectionKeys,
    Item,
    Kinds,
    SIZE_PACKS_DELIMITER,
    SIZE_PACKS_EACHES,
    SIZE_PACKS_STATE_PATH,
} from "constants/product-collection";
import {
    sizeManufacturedGetter,
    buildMissingDependenciesKnowingSchema,
    parseSelectedSizes,
    getSelectedSizePacksForItem,
    getSizePacksForItem,
    parseSizePacks,
    allowedValuesToAvailableValues,
    packsTypeValueFormatter,
} from "helpers/product-collection";
import theme from "styles/theme";
import { DropdownSizesEditor } from "./product-collection-views/product-collection-grid-view/grid-editors/dropdown-sizes-editor";
import { ICellEditorParams } from "ag-grid-community";
import { getStore } from "store";
import Button from "components/form/button";
import { findOneFieldByKind } from "epics/collections/helpers/allocation";
import { ProductCollectionItemSizeField } from "./product-collection-item-size-field";
import { Map } from "immutable";
import { DATATEST } from "constants/integration";
import { SingleChoiceCellEditor } from "./product-collection-views/product-collection-grid-view/grid-editors/single-choice-cell-editor";
import { IPackInfo } from "../../reducers/collection";
import { amphibiousGetIn } from "helpers/objUtils";
import { MultipleChoiceCellEditorWrapper } from "./product-collection-views/product-collection-grid-view/grid-editors/multiple-choice-cell-editor-container";
import { PackTypeCellEditorEditorWrapper } from "containers/cell-editors/pack-type-cell-editor-container";

interface IProductCollectionItemSizing {
    item: Map<string, any>;
    collectionId: string;
    onSizeFieldUpdated: (fieldPaths: string[], value: string, args?: any) => void;
    onPacksFieldUpdated: (fieldPaths: string[], value: string) => void;
    sizePackEnabled?: boolean;
    readOnly?: boolean;
}

const cellEditorParams: ICellEditorParams = {
    value: "",
    eventKey: undefined,
    key: undefined,
    charPress: undefined,
    column: undefined,
    colDef: { headerName: "" },
    node: undefined,
    data: undefined,
    rowIndex: undefined,
    columnApi: undefined,
    api: undefined,
    cellStartedEdit: undefined,
    onKeyDown: undefined,
    context: undefined,
    stopEditing: undefined,
    eGridCell: undefined,
    parseValue: undefined,
    formatValue: undefined,
};

export const sizeInfoStyle = [
    $(" .sizeInfo", {
        width: "20%",
        flex: "none !important",
        margin: "8px 0",
        paddingRight: "8px",
    }),

    $(" .sizeInfo > p", {
        fontSize: "14px",
        color: theme.palette.textColor,
    }),
    $(" .sizeInfo > p:first-child", {
        fontWeight: "bold",
    }),
    $(" .field", {
        display: "flex",
        padding: "8px 8px 8px 0",
        wordBreak: "break-word",
        fontSize: "13px",
    }),
];

export const ProductCollectionItemSizing: React.FunctionComponent<IProductCollectionItemSizing> = ({
    item,
    collectionId,
    onSizeFieldUpdated,
    onPacksFieldUpdated,
    sizePackEnabled,
    readOnly,
}) => {
    const sizeFieldStyle = React.useMemo(
        () =>
            merge(
                ...sizeInfoStyle,
                $(" .field svg", {
                    marginLeft: "12px",
                })
            ),
        []
    );

    const styles = React.useMemo(
        () =>
            merge(
                {
                    width: "100%",
                    display: "flex",
                    flexWrap: "wrap !important",
                    justifyContent: "space-between",
                },
                sizeFieldStyle
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const selectedSizesStyles = React.useMemo(
        () => ({
            wrapper: { width: "100%", borderRadius: "4px" },
            label: {
                textAlign: "left",
                display: "block",
                padding: "0px 24px 0 16px",
                textOverflow: "ellipsis",
                overflow: "hidden",
            },
            anchorOrigin: { vertical: "top", horizontal: "left" },
            popover: { minWidth: "220px", maxWidth: "250px" },
        }),
        []
    );

    const getSizeFieldValue = React.useCallback((fieldPaths) => item.getIn(fieldPaths) || "", [item]);

    const sizeSelectedFromItem: string = getSizeFieldValue(Item.FIELDS.SIZES_SELECTED);

    const packTypeSelectedFromItem: string = getSizeFieldValue(Item.FIELDS.PACK_TYPE);

    const packPerProductSelectedFromItem: string = getSizeFieldValue(Item.FIELDS.PACKS_PER_PRODUCT);

    const packPerDoorSelectedFromItem: string = getSizeFieldValue(Item.FIELDS.PACKS_PER_DOOR);

    const packSelectedFromItem: string = getSizeFieldValue(Item.FIELDS.PACKS_SELECTED);

    const [sizeSelected, setSizeSelected] = React.useState(sizeSelectedFromItem);
    const [packTypeSelected, setPackTypeSelected] = React.useState(packTypeSelectedFromItem);
    const [packSelected, setPackSelected] = React.useState(packSelectedFromItem);
    const [packPerProductSelected, setPackPerProductSelected] = React.useState(packPerProductSelectedFromItem);
    const [packPerDoorSelected, setPackPerDoorSelected] = React.useState(packPerDoorSelectedFromItem);
    const [sizeRanges, setSizeSizeRanges] = React.useState([]); // set by dropdown-sizes-container.tsx

    React.useEffect(() => {
        setSizeSelected(sizeSelectedFromItem);
        setPackTypeSelected(packTypeSelectedFromItem);
        setPackPerProductSelected(packPerProductSelectedFromItem);
        setPackPerDoorSelected(packPerDoorSelectedFromItem);
        setPackSelected(packSelectedFromItem);
    }, [
        sizeSelectedFromItem,
        packTypeSelectedFromItem,
        packSelectedFromItem,
        packPerProductSelectedFromItem,
        packPerDoorSelectedFromItem,
    ]);

    const {
        merchantId,
        getMissingDependencies,
        rawItemDisplayProperty,
        selectedSizesDelimiter,
        selectedSizesDisplayName,
    } = React.useMemo(() => {
        const { mutate } = getStore().getState();
        const currentCollection = mutate.snapshots && mutate.snapshots.get(collectionId);
        const itemDisplayProperty = currentCollection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS);
        const _rawitemDisplayProperty = itemDisplayProperty.toJS();
        const selectedSizesColumn = findOneFieldByKind(Kinds.SIZES_SELECTED, _rawitemDisplayProperty);
        return {
            merchantId: currentCollection.getIn(["merchant_v1_id", "string_value"], ""),
            getMissingDependencies: buildMissingDependenciesKnowingSchema(selectedSizesColumn, _rawitemDisplayProperty),
            rawItemDisplayProperty: _rawitemDisplayProperty,
            selectedSizesDelimiter: selectedSizesColumn.field_value_delimiter,
            selectedSizesDisplayName: selectedSizesColumn?.display_name,
        };
    }, [item, collectionId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlerSizeSelectedChanged = React.useCallback((value, _sizeRanges) => {
        setSizeSelected(value || "");
        setSizeSizeRanges(_sizeRanges);
    }, []);

    const handlerCloseSizeSelectedMenu = React.useCallback(() => {
        if (sizeSelectedFromItem !== sizeSelected) {
            onSizeFieldUpdated(Item.FIELDS.SIZES_SELECTED, sizeSelected, {
                rawItemDisplayProperty,
                sizeManufactured: sizeManufacturedGetter(null, { data: item.toJS() }),
                sizeRanges,
            });
        }
    }, [sizeSelected, sizeRanges, sizeSelectedFromItem]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectPackType = React.useCallback(
        (packType) => {
            if (packTypeSelectedFromItem !== packType) {
                setPackTypeSelected(packType);
                onPacksFieldUpdated(Item.FIELDS.PACK_TYPE, packType);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setPackTypeSelected, packTypeSelectedFromItem]
    );

    const handlerPackSelectedChanged = React.useCallback((value) => {
        setPackSelected(value || "");
    }, []);

    const selectPackPerProduct = React.useCallback(
        (packPerProduct) => {
            if (packPerProductSelectedFromItem !== packPerProduct) {
                setPackPerProductSelected(packPerProduct);
                onPacksFieldUpdated(Item.FIELDS.PACKS_PER_PRODUCT, packPerProduct);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setPackPerProductSelected, packPerProductSelectedFromItem]
    );

    const selectPackPerDoor = React.useCallback(
        (packPerDoor) => {
            if (packPerDoorSelectedFromItem !== packPerDoor) {
                setPackPerDoorSelected(packPerDoor);
                onPacksFieldUpdated(Item.FIELDS.PACKS_PER_DOOR, packPerDoor);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setPackPerDoorSelected, packPerDoorSelectedFromItem]
    );

    const handlerClosePackSelectedMenu = React.useCallback(() => {
        if (packSelectedFromItem !== packSelected) {
            onPacksFieldUpdated(Item.FIELDS.PACKS_SELECTED, packSelected);
        }
    }, [packSelected, packSelectedFromItem]); // eslint-disable-line react-hooks/exhaustive-deps

    const getAvailableValuesPackType = React.useCallback(
        (stateChunk, itemFields) => {
            const brandId = amphibiousGetIn(itemFields, Item.FIELDS.BRAND_ID);
            const allowedPacks = parseSizePacks(amphibiousGetIn(itemFields, Item.FIELDS.PACK_IDS));
            const availablePackTypes = allowedPacks
                .map((pack) => stateChunk.collection.sizePacks[brandId]?.[pack]?.packType)
                .filter((pack) => !!pack);

            return allowedValuesToAvailableValues(
                packTypeColumn.constraint?.allowed_values
                    ?.filter(({ string_value }) => string_value === "" || availablePackTypes.includes(string_value))
                    .map(({ string_value: value }) => ({
                        id: value,
                        string_value: packsTypeValueFormatter(undefined, { value }),
                    }))
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [amphibiousGetIn, parseSizePacks, allowedValuesToAvailableValues, packsTypeValueFormatter]
    );

    const getAvailableValuesPackPerDoor = React.useCallback(
        (stateChunk, itemFields) => {
            const packsPerProd = amphibiousGetIn(itemFields, Item.FIELDS.PACKS_PER_PRODUCT);
            if (!packsPerProd && packsPerProd !== 0) {
                return [];
            }
            return rangeFromAtoB(1, +packsPerProd).map((v) => ({ id: `${v}`, label: v, enabled: true }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rangeFromAtoB, getSelectedSizePacksForItem]
    );

    const getAvailableValuesPackSelected = React.useCallback(
        (stateChunk, itemFields = item) => {
            const packType = amphibiousGetIn(itemFields, Item.FIELDS.PACK_TYPE);
            const enabledSizePacks = packType && SIZE_PACKS_EACHES !== packType;
            if (stateChunk) {
                if (enabledSizePacks) {
                    const brandId = amphibiousGetIn(itemFields, Item.FIELDS.BRAND_ID);
                    const allowedPacks = getSizePacksForItem(itemFields, stateChunk.collection.sizePacks, packType);
                    return transformPacksToAvailableValues(stateChunk.collection.sizePacks[brandId]).filter(({ id }) =>
                        allowedPacks.includes(id)
                    );
                }
                return [];
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [amphibiousGetIn, getSizePacksForItem, transformPacksToAvailableValues]
    );

    const getAvailableValuesPackPerProduct = React.useCallback(
        (stateChunk, itemFields) => {
            return rangeFromAtoB(1, getSelectedSizePacksForItem(itemFields).length).map((v) => ({
                id: `${v}`,
                label: v,
                enabled: true,
            }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [amphibiousGetIn, rangeFromAtoB]
    );

    const COLON = ":";

    const sizeMethodColumn = findOneFieldByKind(Kinds.SIZE_CURVE_METHOD, rawItemDisplayProperty);
    const sizeProfileColumn = findOneFieldByKind(Kinds.SIZE_PROFILE_SELECTED, rawItemDisplayProperty);
    const curveStatusColumn = findOneFieldByKind(Kinds.SIZE_CURVE_STATUS, rawItemDisplayProperty);
    const packPerDoorColum = findOneFieldByKind(Kinds.PACKS_MAX_PER_DOOR, rawItemDisplayProperty);
    const packPerProductColumn = findOneFieldByKind(Kinds.PACKS_MAX_PER_PROD, rawItemDisplayProperty);
    const packTypeColumn = findOneFieldByKind(Kinds.PACKS_TYPE, rawItemDisplayProperty);
    const packSelectedColumn = findOneFieldByKind(Kinds.PACKS_SELECTED, rawItemDisplayProperty);
    const packNotesColumn = rawItemDisplayProperty.find((data) => data.field_name === Item.FIELDS.PACKS_NOTES[1]);
    const getSelectedPacksName = () => {
        if (getSelectedSizePacksForItem(item).length) {
            const brandId = amphibiousGetIn(item, Item.FIELDS.BRAND_ID);
            const activePackNames = getSelectedSizePacksForItem(item)
                .map((pack) => {
                    const packInfo = amphibiousGetIn(
                        getStore().getState(),
                        SIZE_PACKS_STATE_PATH.concat([brandId, pack])
                    );
                    return packInfo?.name;
                })
                .filter((pack) => !!pack);
            if (activePackNames.length) {
                return `(${activePackNames.length}) ${activePackNames.join(", ")}`;
            }
        }
    };

    const overlayAction = () => {
        const muiOverlayElements = document.getElementsByClassName("MuiPopover-root button-menu");
        if (muiOverlayElements && muiOverlayElements.length) {
            const overlay = Array.from(muiOverlayElements[0].children)[0] as any;
            overlay.click();
        }
    };

    return (
        <div data-test={DATATEST.ASSORTMENTS.CDP.SIZE_HEADER_CONTAINER} {...styles}>
            <ProductCollectionItemSizeField
                label={formatMessage("Vendor Sizes") + COLON}
                value={sizeManufacturedGetter(null, { data: item.toJS() })}
                styles={sizeFieldStyle}
            />
            {readOnly && (
                <ProductCollectionItemSizeField
                    label={selectedSizesDisplayName + COLON}
                    value={parseSelectedSizes(sizeSelected).allSizes.join(selectedSizesDelimiter)}
                    styles={sizeFieldStyle}
                />
            )}
            {!readOnly && (
                <div data-test={DATATEST.ASSORTMENTS.CDP.SELECTED_SIZES_HEADER} className={"sizeInfo"}>
                    <Typography>{selectedSizesDisplayName + COLON}</Typography>
                    <div data-test={DATATEST.ASSORTMENTS.CDP.SELECTED_SIZES_VALUE} className="field">
                        <Button
                            key="dropdown-button"
                            label={parseSelectedSizes(sizeSelected).allSizes.join(selectedSizesDelimiter) || " "}
                            style={selectedSizesStyles.wrapper}
                            labelStyle={selectedSizesStyles.label}
                            segmented={false}
                            anchorOrigin={selectedSizesStyles.anchorOrigin}
                            popoverStyle={selectedSizesStyles.popover}
                            onCloseMenu={handlerCloseSizeSelectedMenu}
                            menu={
                                <DropdownSizesEditor
                                    {...cellEditorParams}
                                    value={sizeSelected}
                                    merchantId={merchantId}
                                    data={item.toJS()}
                                    getMissingDependencies={getMissingDependencies}
                                    onSizeSelectedChanged={handlerSizeSelectedChanged}
                                    delimiter={selectedSizesDelimiter}
                                />
                            }
                        />
                    </div>
                </div>
            )}
            <ProductCollectionItemSizeField
                label={sizeMethodColumn?.display_name + COLON}
                value={getSizeFieldValue(Item.FIELDS.SIZE_CURVE_METHOD)}
                fieldSchema={sizeMethodColumn}
                styles={sizeFieldStyle}
            />
            <ProductCollectionItemSizeField
                label={sizeProfileColumn?.display_name + COLON}
                value={getSizeFieldValue(Item.FIELDS.SIZE_CURVE)}
                fieldSchema={sizeProfileColumn}
                styles={sizeFieldStyle}
            />

            <ProductCollectionItemSizeField
                label={curveStatusColumn?.display_name + COLON}
                value={getSizeFieldValue(Item.FIELDS.SIZE_CURVE_STATUS)}
                fieldSchema={curveStatusColumn}
                styles={sizeFieldStyle}
            />

            {packNotesColumn && (
                <ProductCollectionItemSizeField
                    label={packNotesColumn?.display_name + COLON}
                    value={getSizeFieldValue(Item.FIELDS.PACKS_NOTES)}
                    fieldSchema={packNotesColumn}
                    styles={sizeFieldStyle}
                />
            )}

            {readOnly && sizePackEnabled && packTypeColumn && (
                <ProductCollectionItemSizeField
                    label={packTypeColumn?.display_name + COLON}
                    value={packsTypeValueFormatter(undefined, { value: packTypeSelected }) || " "}
                    styles={sizeFieldStyle}
                />
            )}
            {!readOnly && sizePackEnabled && packTypeColumn && (
                <div className={"sizeInfo"}>
                    <Typography>{packTypeColumn?.display_name + COLON}</Typography>
                    <div className="field">
                        <Button
                            key="dropdown-button"
                            label={packsTypeValueFormatter(undefined, { value: packTypeSelected }) || " "}
                            style={selectedSizesStyles.wrapper}
                            labelStyle={selectedSizesStyles.label}
                            segmented={false}
                            anchorOrigin={selectedSizesStyles.anchorOrigin}
                            popoverStyle={selectedSizesStyles.popover}
                            disabled={readOnly}
                            menu={
                                <PackTypeCellEditorEditorWrapper
                                    data={item}
                                    getAvailableValues={getAvailableValuesPackType}
                                    onSelectedChoice={(choice) => {
                                        selectPackType(choice);
                                        overlayAction();
                                    }}
                                    value={packTypeSelected}
                                    statePaths={[SIZE_PACKS_STATE_PATH]}
                                />
                            }
                        />
                    </div>
                </div>
            )}

            {readOnly && sizePackEnabled && packSelectedColumn && (
                <ProductCollectionItemSizeField
                    label={packSelectedColumn?.display_name + COLON}
                    value={getSelectedPacksName() || " "}
                    styles={sizeFieldStyle}
                />
            )}
            {!readOnly && sizePackEnabled && packSelectedColumn && (
                <div className={"sizeInfo"}>
                    <Typography>{packSelectedColumn?.display_name + COLON}</Typography>
                    <div className="field">
                        <Button
                            key="dropdown-button"
                            label={getSelectedPacksName() || " "}
                            style={selectedSizesStyles.wrapper}
                            labelStyle={selectedSizesStyles.label}
                            segmented={false}
                            anchorOrigin={selectedSizesStyles.anchorOrigin}
                            popoverStyle={selectedSizesStyles.popover}
                            onCloseMenu={handlerClosePackSelectedMenu}
                            disabled={readOnly}
                            menu={
                                <MultipleChoiceCellEditorWrapper
                                    {...cellEditorParams}
                                    data={item}
                                    getAvailableValues={getAvailableValuesPackSelected}
                                    delimiter={SIZE_PACKS_DELIMITER}
                                    statePaths={[SIZE_PACKS_STATE_PATH]}
                                    onSelectedChanged={handlerPackSelectedChanged}
                                    value={packSelected}
                                />
                            }
                        />
                    </div>
                </div>
            )}

            {readOnly && sizePackEnabled && packPerProductColumn && !packPerProductColumn.hidden && (
                <ProductCollectionItemSizeField
                    label={packPerProductColumn?.display_name + COLON}
                    value={packPerProductSelected || " "}
                    styles={sizeFieldStyle}
                />
            )}
            {!readOnly && sizePackEnabled && packPerProductColumn && !packPerProductColumn.hidden && (
                <div className={"sizeInfo"}>
                    <Typography>{packPerProductColumn?.display_name + COLON}</Typography>
                    <div className="field">
                        <Button
                            key="dropdown-button"
                            label={packPerProductSelected || " "}
                            style={selectedSizesStyles.wrapper}
                            labelStyle={selectedSizesStyles.label}
                            segmented={false}
                            anchorOrigin={selectedSizesStyles.anchorOrigin}
                            popoverStyle={selectedSizesStyles.popover}
                            disabled={readOnly}
                            menu={
                                <SingleChoiceCellEditor
                                    data={item}
                                    getAvailableValues={getAvailableValuesPackPerProduct}
                                    onSelectedChoice={(choice) => {
                                        selectPackPerProduct(choice);
                                        overlayAction();
                                    }}
                                    value={packPerProductSelected}
                                />
                            }
                        />
                    </div>
                </div>
            )}

            {readOnly && sizePackEnabled && packPerDoorColum && !packPerDoorColum.hidden && (
                <ProductCollectionItemSizeField
                    label={packPerDoorColum?.display_name + COLON}
                    value={packPerDoorSelected || " "}
                    styles={sizeFieldStyle}
                />
            )}
            {!readOnly && sizePackEnabled && packPerDoorColum && !packPerDoorColum.hidden && (
                <div className={"sizeInfo"}>
                    <Typography>{packPerDoorColum?.display_name + COLON}</Typography>
                    <div className="field">
                        <Button
                            key="dropdown-button"
                            label={packPerDoorSelected || " "}
                            style={selectedSizesStyles.wrapper}
                            labelStyle={selectedSizesStyles.label}
                            segmented={false}
                            anchorOrigin={selectedSizesStyles.anchorOrigin}
                            popoverStyle={selectedSizesStyles.popover}
                            disabled={readOnly}
                            menu={
                                <SingleChoiceCellEditor
                                    data={item}
                                    getAvailableValues={getAvailableValuesPackPerDoor}
                                    onSelectedChoice={(choice) => {
                                        selectPackPerDoor(choice);
                                        overlayAction();
                                    }}
                                    value={packPerDoorSelected}
                                />
                            }
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
const rangeFromAtoB = (a: number, b: number) => (a === b ? [a] : a > b ? [] : [a].concat(rangeFromAtoB(a + 1, b)));
const transformPacksToAvailableValues = (packs?: { [key: string]: IPackInfo }) =>
    !packs ? [] : Object.values(packs).map(({ id, name: label, active: enabled }) => ({ id, label, enabled }));
